import {
  Box,
  Button,
  FormControl,
  Grid2,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ManagementSettingsInfo } from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { MessageModal } from "./MessageModal";
import { DataContext } from "Contexts/DataProvider";
import { createTitle } from "Utils/format";
import { PiCaretDoubleUpFill } from "react-icons/pi";
import { PiCaretDoubleDownFill } from "react-icons/pi";

export const AdvancedConfiguration = () => {
  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { setChoiceMenu, toggleMenu } = sidebarContext;

  // サイドバー企業プルダウン用
  const companyListContext = useContext(DataContext);
  if (!companyListContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }

  const theme = useTheme();

  // 新規登録モーダル開閉

  const initData: ManagementSettingsInfo = {
    value: "",
    category: "",
    key: "",
  };

  // 通信不可判定時間
  const [recvTimeoutSec, setRecvTimeoutSec] =
    useState<ManagementSettingsInfo>(initData);

  const [initRecvTimeoutSec, setInitRecvTimeoutSec] =
    useState<ManagementSettingsInfo>(initData);

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // タイトル作成
  const title = "詳細設定";
  useEffect(() => {
    document.title = createTitle(title);
    setChoiceMenu(title);
    toggleMenu(title);
  }, [setChoiceMenu, toggleMenu]);

  const initGetData = useCallback(async () => {
    getRecvTimeoutSec();
  }, []);

  // 初期表示時データ取得処理
  useEffect(() => {
    initGetData();
  }, [initGetData]);

  // 通信不可判定時間データ取得処理
  const getRecvTimeoutSec = async () => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/AdvancedConfiguration/GetManagementSettings`,
        {
          params: { category: "recvTimeoutSec" },
        }
      );
      setRecvTimeoutSec(response.data[0]);
      setInitRecvTimeoutSec(response.data[0]);
    } catch (error) {
      setModalOpen(true);
      setMessage("通信不可判定時間情報がありません。");
    }
  };

  const handleUpdateRecvTimeoutSec = async () => {
    //変更有無チェック
    if (recvTimeoutSec.value === initRecvTimeoutSec.value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(recvTimeoutSec, "通信不可判定時間");
  };

  // 更新時処理
  const handleUpdate = async (
    updateData: ManagementSettingsInfo,
    target: string
  ) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/AdvancedConfiguration/PutManagementSettings`,
        updateData
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage(`${target}を更新しました。`);
        initGetData();
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
        setModalOpen(true);
        setMessage(error.response.data);
      } else {
        setModalOpen(true);
        setMessage(`${target}の更新に失敗しました。`);
        console.error(`${target}の更新に失敗しました。`, error);
      }
    }
  };

  const increment = () => {
    setRecvTimeoutSec({
      ...recvTimeoutSec,
      value: recvTimeoutSec.value
        ? (parseInt(recvTimeoutSec.value, 10) + 10).toString()
        : "10",
    });
  };

  const decrement = () => {
    setRecvTimeoutSec({
      ...recvTimeoutSec,
      value: recvTimeoutSec.value
        ? Math.max(0, parseInt(recvTimeoutSec.value, 10) - 10).toString()
        : "0",
    });
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Grid2 container>
            {/* 通信不可判定時間 */}
            <TableContainer component={Paper}>
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableBody>
                  <TableRow>
                    <TableCell
                      size="small"
                      component="th"
                      scope="row"
                      align="center"
                      sx={{
                        fontWeight: "fontWeightRegular",
                        backgroundColor: theme.palette.custom.tableHeader,
                        color: "white",
                        width: "40%",
                      }}
                    >
                      通信不可判定時間
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.custom.row,
                        px: 0,
                        mx: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: theme.palette.custom.row,
                          px: 0,
                          mx: 0,
                        }}
                      >
                        <FormControl sx={{ ml: 0, pl: 0, width: "150px" }}>
                          <TextField
                            variant="outlined"
                            value={recvTimeoutSec.value ?? ""}
                            type="number"
                            size="small"
                            sx={{
                              margin: 0,
                              "& fieldset": { border: "none" },
                            }}
                            inputProps={{ min: 0 }}
                            onChange={(e) => {
                              const newValue =
                                e.target.value === "" ? "0" : e.target.value;

                              setRecvTimeoutSec({
                                ...recvTimeoutSec,
                                value: newValue,
                              });
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    marginTop: "-20px",
                                    marginRight: 0,
                                    paddingRight: 0,
                                  }}
                                >
                                  <IconButton
                                    onClick={increment}
                                    size="small"
                                    sx={{ fontSize: "1em" }}
                                  >
                                    <PiCaretDoubleUpFill fontSize="inherit" />
                                  </IconButton>
                                  <IconButton
                                    onClick={decrement}
                                    size="small"
                                    sx={{ fontSize: "1em" }}
                                  >
                                    <PiCaretDoubleDownFill fontSize="inherit" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormControl>
                        <Box>秒</Box>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        backgroundColor: theme.palette.custom.row,
                        width: { xs: "auto", sm: "20%" },
                        mx: 1,
                        px: 1,
                      }}
                    >
                      <Button
                        onClick={handleUpdateRecvTimeoutSec}
                        sx={{
                          width: {
                            sm: "10vh",
                            md: "13vh",
                            lg: "20vh",
                          },
                        }}
                        variant="contained"
                        color="primary"
                        style={{ backgroundColor: "#000080" }}
                      >
                        更新
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>
        </Box>
      </Box>
    </>
  );
};
export default AdvancedConfiguration;
